// Trace events recorded by the interpreter, in virtual time.

import { Address } from "./value.js";

// Every event carries the 1-based source line of the statement that produced
// it, so the playground can map a trace row back to a line of code.
export type TraceEvent = (
  | { type: "spawn"; time: number; parent: number; child: number; fn: string }
  | { type: "thread-start"; time: number; thread: number; fn: string }
  | { type: "thread-end"; time: number; thread: number; fn: string }
  | { type: "sleep"; time: number; thread: number; duration: number; until: number }
  // `prevTxn` is the previous attempt of the same `txn` statement, when this begin
  // re-runs one: the txn that was abandoned by a `retry` park (and is now
  // "reincarnated" under a new id) or by an abort. Absent on a first attempt.
  | {
      type: "txn-begin";
      time: number;
      thread: number;
      txn: number;
      readVersion: number;
      prevTxn?: number;
    }
  | {
      type: "txn-commit";
      time: number;
      thread: number;
      txn: number;
      commitVersion: number;
      reads: number;
      writes: number;
    }
  | { type: "txn-abort"; time: number; thread: number; txn: number; reason: string; attempt: number }
  // A `retry` inside a txn: the attempt is abandoned and the thread parks on its
  // read set (`reads` = its size) until a committed write overlaps it.
  | { type: "txn-retry"; time: number; thread: number; txn: number; reads: number }
  // An `orElse` branch `retry`d: its tentative writes were rolled back and the next
  // branch is tried (its reads stay in the txn's read set / wake union).
  | { type: "orelse-retry"; time: number; thread: number; txn: number }
  // Pessimistic (2PL) lock events, emitted only under the `hybrid` strategy when
  // a txn's footprint is statically known. `addrs` are the locked root objects.
  | { type: "lock-acquire"; time: number; thread: number; txn: number; addrs: Address[] }
  | { type: "lock-wait"; time: number; thread: number; txn: number; addrs: Address[] }
  | { type: "lock-release"; time: number; thread: number; txn: number; addrs: Address[] }
  | {
      type: "read";
      time: number;
      thread: number;
      txn: number | "auto";
      addr: Address;
      version: number;
      value: string;
    }
  | {
      type: "write";
      time: number;
      thread: number;
      txn: number | "auto";
      addr: Address;
      value: string;
    }
  | { type: "print"; time: number; thread: number; message: string }
  // An `assert cond, "message"`: `passed` is the condition's verdict, `message`
  // its label. Soft — it records the outcome without unwinding — so both branches
  // of a race can each carry their own verdict for the outcome layer to compare.
  | { type: "assert"; time: number; thread: number; passed: boolean; message: string }
) & { line: number };

export class Trace {
  readonly events: TraceEvent[] = [];

  record(ev: TraceEvent): void {
    this.events.push(ev);
  }

  /** A compact, human-readable rendering, one line per event. */
  format(): string {
    return this.events.map(formatEvent).join("\n");
  }

  toJSON(): TraceEvent[] {
    return this.events;
  }
}

function pad(t: number): string {
  return `t=${t.toFixed(2).padStart(6)}`;
}

function formatEvent(ev: TraceEvent): string {
  const thread = "thread" in ev ? `T${ev.thread}` : "";
  switch (ev.type) {
    case "spawn":
      return `${pad(ev.time)} T${ev.parent} spawn -> T${ev.child} ${ev.fn}()`;
    case "thread-start":
      return `${pad(ev.time)} ${thread} start ${ev.fn}()`;
    case "thread-end":
      return `${pad(ev.time)} ${thread} end ${ev.fn}()`;
    case "sleep":
      return `${pad(ev.time)} ${thread} sleep ${ev.duration} -> t=${ev.until.toFixed(2)}`;
    case "txn-begin":
      return `${pad(ev.time)} ${thread} txn#${ev.txn} begin (readVersion=${ev.readVersion})`;
    case "txn-commit":
      return `${pad(ev.time)} ${thread} txn#${ev.txn} COMMIT v${ev.commitVersion} (${ev.reads}r/${ev.writes}w)`;
    case "txn-abort":
      return `${pad(ev.time)} ${thread} txn#${ev.txn} ABORT (${ev.reason}) retry#${ev.attempt}`;
    case "txn-retry":
      return `${pad(ev.time)} ${thread} txn#${ev.txn} RETRY park (${ev.reads}r)`;
    case "orelse-retry":
      return `${pad(ev.time)} ${thread} txn#${ev.txn} orElse: branch retried, trying next`;
    case "lock-acquire":
      return `${pad(ev.time)} ${thread} txn#${ev.txn} lock {${ev.addrs.join(", ")}}`;
    case "lock-wait":
      return `${pad(ev.time)} ${thread} txn#${ev.txn} wait {${ev.addrs.join(", ")}}`;
    case "lock-release":
      return `${pad(ev.time)} ${thread} txn#${ev.txn} unlock {${ev.addrs.join(", ")}}`;
    case "read":
      return `${pad(ev.time)} ${thread} [${txnLabel(ev.txn)}] read  ${ev.addr}@v${ev.version} = ${ev.value}`;
    case "write":
      return `${pad(ev.time)} ${thread} [${txnLabel(ev.txn)}] write ${ev.addr} = ${ev.value}`;
    case "print":
      return `${pad(ev.time)} ${thread} print: ${ev.message}`;
    case "assert":
      return `${pad(ev.time)} ${thread} assert ${ev.passed ? "PASS" : "FAIL"}${ev.message ? `: ${ev.message}` : ""}`;
  }
}

function txnLabel(txn: number | "auto"): string {
  return txn === "auto" ? "auto" : `txn#${txn}`;
}
